import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";

// Fade-in-up keyframe
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/* 
  The entire question container is 100% wide, left aligned.
  We'll center the options themselves IF fewer than 8.
*/
const QuestionContainer = styled.div`
  width: 100%;
  margin: 2rem 0;
  font-family: "Raleway", sans-serif;
`;

const QuestionLabel = styled.h2`
  margin-bottom: 1rem;
  font-weight: 600;
  font-size: 1.25rem;
  text-align: left; /* Always left aligned */
`;

/* 
  OptionGrid:
  - If isSmall, width=50%, margin: 0 auto => centered grid
  - If not, width=100%
*/
const OptionGrid = styled.div`
  width: ${({ isSmall }) => (isSmall ? "50%" : "100%")};
  display: flex;
  flex-wrap: wrap;
  box-sizing: border-box;
`;

/* 
  OptionLabel:
  - If isSmall: each label is 100% (single column within that 50% grid)
  - Otherwise: each label is 50% (two columns within that 100% grid)
*/
const OptionLabel = styled.label`
  flex: 0 0 ${({ isSmall }) => (isSmall ? "100%" : "calc(50% - 10px)")};
  margin-right: 5px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? "#E8F5E9" : "#f9f9f9")};
  border: 2px solid ${({ selected }) => (selected ? "#4caf50" : "#ccc")};
  border-radius: 8px;
  color: ${({ selected }) => (selected ? "#388E3C" : "#333")};
  padding: 0.75rem;
  margin-bottom: 1rem;

  /* Fade-in-up animation with a small stagger */
  opacity: 0;
  animation: ${fadeInUp} 0.3s ease forwards;
  animation-delay: ${({ delay }) => delay}s;

  &:hover {
    border-color: #4caf50;
  }
`;

// Hidden native checkbox (we'll style a custom box)
const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  opacity: 0;
  position: absolute;
  pointer-events: none;
`;

const CustomCheckbox = styled.span`
  display: inline-block;
  min-width: 1.2rem;
  min-height: 1.2rem;
  margin-right: 0.75rem;
  border: 2px solid ${({ checked }) => (checked ? "#388E3C" : "#ccc")};
  background-color: ${({ checked }) => (checked ? "#4caf50" : "transparent")};
  border-radius: 4px;
  position: relative;
  transition: background-color 0.2s, border-color 0.2s;

  /* White check "tick" */
  &::after {
    content: "";
    position: absolute;
    left: 4px;
    top: 0;
    width: 4px;
    height: 8px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    opacity: ${({ checked }) => (checked ? 1 : 0)};
    transition: opacity 0.2s;
  }
`;

const OptionText = styled.span`
  font-size: 1rem;
`;


const Description = styled.div`
  font-weight:300;
  font-size:12px;
  margin-top:10px;
  font-family:'Raleway', sans-serif;
  margin-bottom:10px;
`


const Qualifier = styled.div`
  font-size:16px;
  font-weight:bold;
  margin-bottom:10px;
`

// Custom input for "Other" option
const OtherInputContainer = styled.div`
  width: ${({ isSmall }) => (isSmall ? "50%" : "100%")};
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: ${fadeInUp} 0.3s ease forwards;
  animation-delay: ${({ delay }) => delay}s;
`;

const OtherInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #4caf50;
  border-radius: 8px;
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.3);
  }
`;

export default function QuestionMultiSelect({
  question,
  currentAnswer,
  onAnswerChange,
}) {
  const options = question.properties.options || [];
  const answerArray = Array.isArray(currentAnswer.response) ? currentAnswer.response : [];
  
  // State for "Other" option text input
  const [otherValue, setOtherValue] = useState('');
  
  // Check if "Other (specify own)" option is enabled and included in options
  const hasOtherOption = question.properties["Add 'Other (specify own)' option"];
  const otherOptionText = "Other (specify own)";
  const isOtherSelected = answerArray.some(opt => opt.includes(otherOptionText));

  // Extract other text from response if it exists
  useEffect(() => {
    if (isOtherSelected) {
      const otherEntry = answerArray.find(opt => opt.startsWith(otherOptionText));
      if (otherEntry && otherEntry.includes(': ')) {
        setOtherValue(otherEntry.split(': ')[1]);
      }
    }
  }, []);

  // Determine layout based on option count
  const isSmall = options.length < 8;

  const handleCheck = (opt, checked) => {
    let newVal = [...answerArray];
    
    if (opt === otherOptionText) {
      // Handle "Other" option differently
      if (checked) {
        // Add with current text if available
        if (otherValue) {
          newVal.push(`${otherOptionText}: ${otherValue}`);
        } else {
          newVal.push(otherOptionText);
        }
      } else {
        // Remove any "Other" entries
        newVal = newVal.filter((o) => !o.includes(otherOptionText));
      }
    } else {
      // Handle regular options
      if (checked) {
        newVal.push(opt);
      } else {
        newVal = newVal.filter((o) => o !== opt);
      }
    }
    
    onAnswerChange(question.uniqueId, {response:newVal});
  };

  // Handle changes to the "Other" text input
  const handleOtherInputChange = (e) => {
    const newValue = e.target.value;
    setOtherValue(newValue);
    
    // Update the response with the new "Other" text
    let newVal = [...answerArray];
    
    // Remove the existing "Other" entry
    newVal = newVal.filter((o) => !o.includes(otherOptionText));
    
    // Add the new "Other" entry with updated text
    if (newValue) {
      newVal.push(`${otherOptionText}: ${newValue}`);
    } else {
      newVal.push(otherOptionText);
    }
    
    onAnswerChange(question.uniqueId, {response:newVal});
  };

  return (
    <QuestionContainer>
    {question.qualifer && (<Qualifier>{question.qualifer}</Qualifier>)}
      <QuestionLabel>{question.question}</QuestionLabel>
      {question.description && <Description>{question.description}</Description>}
      <OptionGrid isSmall={isSmall}>
        {options.map((opt, idx) => {
          const selected = opt === otherOptionText 
            ? isOtherSelected 
            : answerArray.includes(opt);
          
          return (
            <OptionLabel
              key={opt}
              selected={selected}
              isSmall={isSmall}
              delay={idx * 0.1}
            >
              <HiddenCheckbox
                checked={selected}
                onChange={(e) => handleCheck(opt, e.target.checked)}
              />
              <CustomCheckbox checked={selected} />
              <OptionText>{opt}</OptionText>
            </OptionLabel>
          );
        })}
      </OptionGrid>
      
      {/* Show text input when "Other" is selected */}
      {isOtherSelected && (
        <OtherInputContainer 
          isSmall={isSmall} 
          delay={options.length * 0.1 + 0.1} // Set delay to be just after the last option
        >
          <OtherInput
            type="text"
            value={otherValue}
            onChange={handleOtherInputChange}
            placeholder="Please specify..."
            autoFocus
          />
        </OtherInputContainer>
      )}
    </QuestionContainer>
  );
}
